import { FormikHelpers, FormikProps } from 'formik';
import {
  API_FUNCTION_TYPE,
  API_ACCOUNT_CONTRACT_PERSONAL_DATA,
  API_ACCOUNT_DETAIL_CLIENT,
  API_ACCOUNT_VALIDATION_NUMBER,
  API_ACCOUNT_VALIDATION_DATE,
  API_ACCOUNT_CONTRACT_PERSONAL_DATA_ID,
  API_ACCOUNT_DETAIL_CLIENT_NOTIFICATION,
} from 'api/endpoints';
import { action, observable } from 'mobx';
import { RootStore } from 'stores/rootStore';
import { FormStore } from 'stores/formStore';
import { ACCOUNTS_EDIT_FEES } from 'pages/Accounts/Edit/accountEditRoutes';
import history from 'helpers/routerHistory';
import { ContractPhysicalData, ContractLegalData, Notifications } from './model';
import { ContractTab } from 'pages/Accounts/Edit/Contract/Contract';
import { SingleValueResponse } from 'common/interfaces';
import { DateTime } from 'luxon';
import { Props as EditFormModalProps } from 'components/Modals/EditFormModal/EditFormModal';
import i18next from 'i18next';
import { PersonType } from 'enums';
import { merge } from 'lodash';

export class WorkflowPersonalDataStore extends FormStore<ContractPhysicalData | ContractLegalData> {
  accountId = '';
  endpoint: API_FUNCTION_TYPE;
  detailMode: boolean;

  @observable editFormModal: EditFormModalProps = {
    isOpen: false,
  };
  @observable prefix = '';

  constructor(rootStore: RootStore, accountId: string, detailMode?: boolean) {
    super(rootStore);
    this.accountId = accountId;
    this.endpoint = detailMode ? API_ACCOUNT_DETAIL_CLIENT : API_ACCOUNT_CONTRACT_PERSONAL_DATA;
    this.detailMode = !!detailMode;
  }

  @action handleNext = async (
    tabChange: Function,
    data: ContractPhysicalData | ContractLegalData,
    formikHelpers: FormikHelpers<ContractPhysicalData | ContractLegalData>,
    goFwd?: () => void,
  ) => {
    const submitted = await this.handleSubmit(data, formikHelpers);
    if (submitted) {
      if (!!goFwd) {
        goFwd();
      } else {
        tabChange(ContractTab.AML);
      }
    }
  };

  @action handleSaveNotifications = async (data: Notifications) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post<SingleValueResponse<boolean>>(
      API_ACCOUNT_DETAIL_CLIENT_NOTIFICATION(this.accountId),
      data,
    );
    this.rootStore.stores.uiStore.setLoading(false);
    return response?.data?.value;
  };

  @action handleBack = () => {
    history.push(ACCOUNTS_EDIT_FEES(this.accountId));
  };

  fetchPersonalData = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.fetchData(`${this.endpoint(this.accountId)}`, {
      resetData: false,
    });
    this.prefix = this.data && this.data.personType === PersonType.LEGAL ? 'representative.' : '';
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action cherrypickRepresentative = async (formikProps: FormikProps<ContractLegalData>) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<ContractLegalData>(`${this.endpoint(this.accountId)}`);
    if (this.data && 'representative' in this.data && response?.data?.representative) {
      this.data = merge({}, formikProps.values, { representative: response.data.representative });
      await this.rootStore.stores.editAccountStore.fetchBasicData(this.accountId);
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action getForeignIdType = async (
    values: ContractPhysicalData | ContractLegalData,
    legal: boolean,
    formikHelpers: FormikHelpers<ContractPhysicalData | ContractLegalData>,
  ) => {
    this.rootStore.stores.uiStore.setLoading(true);
    let citizenship = '';
    if (legal) {
      citizenship = ('representative' in values && values.representative.citizenship) || '';
    } else {
      citizenship = ('citizenship' in values && values.citizenship) || '';
    }
    const response = await this.rootStore.stores.apiStore.post<{ value: string }>(
      API_ACCOUNT_CONTRACT_PERSONAL_DATA_ID(this.accountId, citizenship),
    );
    const value = response?.data?.value || '';

    if (legal) {
      formikHelpers.setFieldValue('representative.foreignIdType', value);
      formikHelpers.setFieldValue('representative.foreignId', '');
      formikHelpers.setFieldValue('representative.personalNumber', '');
    } else {
      formikHelpers.setFieldValue('foreignIdType', value);

      if (!values.readOnly) {
        formikHelpers.setFieldValue('foreignId', '');
        formikHelpers.setFieldValue('personalNumber', '');
      } else {
        formikHelpers.setFieldValue('foreignId', '');
      }
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  async handleSubmit(
    values: ContractPhysicalData | ContractLegalData,
    formikHelpers: FormikHelpers<ContractPhysicalData | ContractLegalData>,
  ): Promise<boolean> {
    const success = await this.submitData(`${this.endpoint(this.accountId)}`, values, {
      formikHelpers,
      singleValueResponse: true,
      validationPrefixDepth: 2,
      reset: false,
    });
    if (success && !this.detailMode) {
      await this.rootStore.stores.editAccountStore.fetchBasicData(this.accountId);
    }
    return success;
  }

  validatePassport = async (formikProps: FormikProps<ContractPhysicalData | ContractLegalData>) => {
    const { post } = this.rootStore.stores.apiStore;
    const physical = 'identificationDocument' in formikProps.values;
    const identificationDocument =
      'identificationDocument' in formikProps.values
        ? formikProps.values.identificationDocument
        : formikProps.values.representative.identificationDocument;
    const { number, type } = identificationDocument;
    if (!number) return;

    const response = await post<SingleValueResponse<boolean>>(API_ACCOUNT_VALIDATION_NUMBER(this.accountId), {
      number,
      type,
    });
    if (response?.data?.value === false) {
      this.editFormModal = {
        isOpen: true,
        onClose: () => (this.editFormModal.isOpen = false),
        children: i18next
          .t('personalData_identificationDocument_invalid')
          .replace(
            '$field$',
            `<strong>${i18next.t('contract_clientId_number')}</strong>. ${i18next.t('general_value')} <strong>${number
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')}</strong>`,
          ),
        onEdit: () => {
          formikProps.setFieldValue(
            physical ? 'identificationDocument.number' : 'representative.identificationDocument.number',
            '',
            false,
          );
          this.editFormModal.isOpen = false;
        },
      };
    }
  };

  validateFromToDocument = async (
    validTo: DateTime | null,
    formikProps: FormikProps<ContractPhysicalData | ContractLegalData>,
    validFrom?: DateTime | null,
  ) => {
    const { post } = this.rootStore.stores.apiStore;

    if (validTo && validTo.isValid) {
      const physical = 'identificationDocument' in formikProps.values;
      const identificationDocument =
        'identificationDocument' in formikProps.values
          ? formikProps.values.identificationDocument
          : formikProps.values.representative.identificationDocument;

      const from = validFrom ? validFrom.toSQLDate() : identificationDocument.validFrom;

      const response = await post<SingleValueResponse<boolean>>(API_ACCOUNT_VALIDATION_DATE(this.accountId), {
        from,
        to: validTo.toSQLDate(),
      });
      if (response?.data?.value === false) {
        this.editFormModal = {
          isOpen: true,
          onClose: () => (this.editFormModal.isOpen = false),
          children: i18next
            .t('personalData_identificationDocument_invalid')
            .replace(
              '$field$',
              `<strong>${i18next.t('general_validTo')}</strong>. ${i18next.t(
                'general_value',
              )}: <strong>${validTo.toLocaleString()}</strong>`,
            ),
          onEdit: () => {
            formikProps.setFieldValue(
              physical ? 'identificationDocument.validTo' : 'representative.identificationDocument.validTo',
              '',
              false,
            );
            this.editFormModal.isOpen = false;
          },
        };
      }
    }
  };
}
